import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import Select from '../../../../components/ui/Select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '../../../../components/ui/Dialog';
import {
  parseExcelPreview,
  bulkImportMatches,
  analyzeBulkImport,
  downloadBulkImportTemplate,
} from '@/api/TournamentManagement/matchApi';
import {
  MatchFormat,
  MatchType,
  BulkImportRowAction,
  UnresolvedOfficialReason,
  resolveEnum,
} from '@/models/tournament';
import type {
  BulkImportPreviewView,
  MatchBulkImportResultView,
  BulkImportRowResult,
  BulkImportAnalyzeResultView,
  UnresolvedOfficialView,
  ApproximateMatchView,
} from '@/models/tournament';

// ── Types ──────────────────────────────────────────────────────────────────

interface BulkImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  tournamentId: number;
  onSuccess: () => void;
}

type Phase = 'upload' | 'preview' | 'result';

// A row imports with Cancelled status when a STATUS column says so, or the venue
// cell carries a cancellation marker. Mirrors the backend's TryDetectCancelled.
const isRowCancelled = (row: { venue?: string; status?: string }): boolean =>
  `${row.status ?? ''} ${row.venue ?? ''}`.toUpperCase().includes('CANCEL');

// ── Component ──────────────────────────────────────────────────────────────

const BulkImportModal: React.FC<BulkImportModalProps> = ({
  isOpen,
  onClose,
  tournamentId,
  onSuccess,
}) => {
  const { t } = useTranslation('tournament');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Phase state
  const [phase, setPhase] = useState<Phase>('upload');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Upload phase
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Preview phase
  const [preview, setPreview] = useState<BulkImportPreviewView | null>(null);
  const [weekStartDate, setWeekStartDate] = useState('');
  const [defaultFormat, setDefaultFormat] = useState<string>(String(MatchFormat.ElevenAside));
  const [defaultType, setDefaultType] = useState<string>(String(MatchType.Outdoor));

  // Analyze (preview classification) state
  const [analysis, setAnalysis] = useState<BulkImportAnalyzeResultView | null>(null);
  const [analyzing, setAnalyzing] = useState(false);

  // Result phase
  const [result, setResult] = useState<MatchBulkImportResultView | null>(null);

  // Build a quick lookup so we can render an action badge per row
  const resultsByRow = useMemo(() => {
    const map = new Map<number, BulkImportRowResult>();
    if (analysis) {
      for (const r of analysis.rowResults) map.set(r.rowIndex, r);
    }
    return map;
  }, [analysis]);

  // Re-analyze when settings change in preview phase (debounced)
  useEffect(() => {
    if (phase !== 'preview' || !preview || !weekStartDate) {
      setAnalysis(null);
      return;
    }
    let cancelled = false;
    setAnalyzing(true);
    const handle = setTimeout(async () => {
      try {
        const response = await analyzeBulkImport({
          input: {
            tournamentId,
            weekStartDate,
            defaultFormat: parseInt(defaultFormat, 10) as MatchFormat,
            defaultType: parseInt(defaultType, 10) as MatchType,
          },
          rows: preview.rows,
        });
        if (cancelled) return;
        if (response.errors.length === 0 && response.payload) {
          setAnalysis(response.payload);
        }
      } catch (err) {
        if (!cancelled) console.error('Analyze failed:', err);
      } finally {
        if (!cancelled) setAnalyzing(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [phase, preview, weekStartDate, defaultFormat, defaultType, tournamentId]);

  // Rows that imported (or will import) carrying one or more non-blocking warnings.
  const warnedRows = useMemo(
    () => (result?.rowResults ?? []).filter((r) => r.warnings && r.warnings.length > 0),
    [result]
  );

  // Which optional columns the uploaded sheet actually carries. A column the file does not use
  // is not rendered at all — an empty column of dashes is noise, not information.
  const hasColumn = useMemo(() => {
    if (!preview) {
      return { headRef: false, ar1: false, ar2: false, fourth: false, format: false, type: false };
    }
    return {
      headRef: preview.rows.some((r) => !!r.headReferee),
      ar1: preview.rows.some((r) => !!r.assistantReferee1),
      ar2: preview.rows.some((r) => !!r.assistantReferee2),
      fourth: preview.rows.some((r) => !!r.fourthOfficial),
      format: preview.rows.some((r) => !!r.format),
      type: preview.rows.some((r) => !!r.type),
    };
  }, [preview]);

  // Drag-and-drop visual state
  const [isDragging, setIsDragging] = useState(false);

  // Template download
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      await downloadBulkImportTemplate();
    } catch {
      setError(t('Could not download the template. Please try again.'));
    } finally {
      setDownloadingTemplate(false);
    }
  };


  // ── Handlers ──

  const isExcelFile = (file: File) => {
    const name = file.name.toLowerCase();
    return name.endsWith('.xlsx') || name.endsWith('.xls');
  };

  const processFile = async (file: File) => {
    if (!isExcelFile(file)) {
      setError(t('Only .xlsx and .xls files are supported'));
      return;
    }

    setSelectedFile(file);
    setError('');
    setIsLoading(true);

    try {
      const response = await parseExcelPreview(file);
      if (response.errors.length > 0) {
        setError(response.errors[0]?.description || t('Failed to parse Excel file'));
        setIsLoading(false);
        return;
      }
      if (response.payload) {
        setPreview(response.payload);
        setPhase('preview');
      }
    } catch (error: any) {
      const msg = error?.response?.data?.errors?.[0]?.description
        || error?.message
        || t('Something went wrong. Please try again.');
      setError(msg);
      console.error('Error parsing Excel file:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await processFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (isLoading) return;
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    await processFile(file);
  };

  const handleImport = async () => {
    if (!preview || !weekStartDate) {
      setError(t('Please fill in all required fields'));
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      const response = await bulkImportMatches({
        input: {
          tournamentId,
          weekStartDate,
          defaultFormat: parseInt(defaultFormat, 10) as MatchFormat,
          defaultType: parseInt(defaultType, 10) as MatchType,
        },
        rows: preview.rows,
      });

      if (response.errors.length > 0) {
        setError(response.errors[0]?.description || t('Failed to import matches'));
        setIsLoading(false);
        return;
      }

      if (response.payload) {
        setResult(response.payload);
        setPhase('result');
      }
    } catch (error: any) {
      const msg = error?.response?.data?.errors?.[0]?.description
        || error?.message
        || t('Something went wrong. Please try again.');
      setError(msg);
      console.error('Error importing matches:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    // Reset all state
    setPhase('upload');
    setIsLoading(false);
    setError('');
    setSelectedFile(null);
    setPreview(null);
    setWeekStartDate('');
    setDefaultFormat(String(MatchFormat.ElevenAside));
    setDefaultType(String(MatchType.Outdoor));
    setResult(null);
    setAnalysis(null);
    setAnalyzing(false);
    setDownloadingTemplate(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const handleDone = () => {
    onSuccess();
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) handleClose(); }}>
      {/* size="lg" + max-w-3xl keeps the original width; overflow-y-hidden hands scrolling back to
          the body so the header and footer stay put, as they did before. The header X is kept
          (hideCloseButton) so its position and data-testid survive. */}
      <DialogContent
        size="lg"
        hideCloseButton
        className="max-w-3xl flex flex-col overflow-y-hidden"
        data-testid="bulk-import-modal"
      >
        {/* ── Header ── */}
        <DialogHeader className="py-4 shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-lg flex items-center gap-2">
              <Icon name="Upload" size={20} className="text-primary" />
              {t('Import Matches from Excel')}
            </DialogTitle>
            <button
              type="button"
              onClick={handleClose}
              aria-label={t('Close')}
              className="p-2 hover:bg-muted rounded-lg transition-colors focus-visible:ring-2 focus-visible:ring-primary/20"
              data-testid="bulk-import-close-btn"
            >
              <Icon name="X" size={18} />
            </button>
          </div>
        </DialogHeader>

        {/* ── Body ── */}
        <DialogBody className="overflow-y-auto flex-1 space-y-5">

          {/* Phase 1: Upload */}
          {phase === 'upload' && (
            <div className="space-y-4">
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {t('Upload an Excel file (.xlsx) of match data. Columns read: Day, Venue, Pitch, Time, Age Category, Division, Team 1, Team 2, Format, Type, Status, and the officials. A row missing its day, time or a team still imports, flagged with a warning.', { nsSeparator: false })}
                </p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <button
                    type="button"
                    onClick={handleDownloadTemplate}
                    disabled={downloadingTemplate}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-primary rounded transition-colors hover:text-primary/80 disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                    data-testid="bulk-import-download-template"
                  >
                    <Icon
                      name={downloadingTemplate ? 'Loader2' : 'Download'}
                      size={15}
                      className={downloadingTemplate ? 'animate-spin' : undefined}
                    />
                    {t('Download the template')}
                  </button>
                  <span className="text-xs text-muted-foreground">
                    {t('Every column, with a sheet explaining what each one accepts.')}
                  </span>
                </div>
              </div>

              <div
                role="button"
                tabIndex={0}
                aria-label={t('Drop an Excel file here or click to browse')}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer focus-visible:ring-2 focus-visible:ring-primary/20 ${
                  isDragging
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
                onDragOver={handleDragOver}
                onDragEnter={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                data-testid="bulk-import-dropzone"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="bulk-import-file-input"
                />
                {isLoading ? (
                  <div className="flex flex-col items-center gap-3">
                    <Icon name="Loader2" size={40} className="text-primary animate-spin" />
                    <p className="text-sm font-medium text-foreground">{t('Parsing file...')}</p>
                  </div>
                ) : isDragging ? (
                  <div className="flex flex-col items-center gap-3">
                    <Icon name="Download" size={40} className="text-primary" />
                    <p className="text-sm font-medium text-foreground">{t('Drop the file to upload')}</p>
                    <p className="text-xs text-muted-foreground">{t('excelFileExtensions')}</p>
                  </div>
                ) : selectedFile ? (
                  <div className="flex flex-col items-center gap-3">
                    <Icon name="FileSpreadsheet" size={40} className="text-primary" />
                    <p className="text-sm font-medium text-foreground">{selectedFile.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {t('Drop a new file or click to browse')}
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <Icon name="Upload" size={40} className="text-muted-foreground" />
                    <p className="text-sm font-medium text-foreground">
                      {t('Drag and drop an Excel file here, or click to browse')}
                    </p>
                    <p className="text-xs text-muted-foreground">{t('excelFileExtensions')}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Phase 2: Preview & Configure */}
          {phase === 'preview' && preview && (
            <div className="space-y-5">
              {/* Summary */}
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <span className="flex items-center gap-1.5 text-foreground">
                  <Icon name="FileSpreadsheet" size={16} className="text-primary" />
                  {t('{{count}} rows parsed', { count: preview.totalRows })}
                </span>
                {analysis ? (
                  <>
                    <span className="flex items-center gap-1.5 text-green-600">
                      <Icon name="PlusCircle" size={16} />
                      {t('{{count}} new', { count: analysis.newCount })}
                    </span>
                    <span className="flex items-center gap-1.5 text-blue-600">
                      <Icon name="RefreshCw" size={16} />
                      {t('{{count}} update', { count: analysis.updateCount })}
                    </span>
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      <Icon name="MinusCircle" size={16} />
                      {t('{{count}} skip', { count: analysis.skipCount })}
                    </span>
                    {analysis.warningCount > 0 && (
                      <span className="flex items-center gap-1.5 text-amber-600">
                        <Icon name="AlertTriangle" size={16} />
                        {t('{{count}} with warnings', { count: analysis.warningCount })}
                      </span>
                    )}
                    {analysis.errorCount > 0 && (
                      <span className="flex items-center gap-1.5 text-destructive">
                        <Icon name="AlertCircle" size={16} />
                        {t('{{count}} error', { count: analysis.errorCount })}
                      </span>
                    )}
                  </>
                ) : analyzing ? (
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <Icon name="Loader2" size={16} className="animate-spin" />
                    {t('Analyzing rows...')}
                  </span>
                ) : !weekStartDate ? (
                  <span className="flex items-center gap-1.5 text-amber-600">
                    <Icon name="Info" size={16} />
                    {t('Pick a week start date to see new vs update breakdown')}
                  </span>
                ) : null}
              </div>

              {/* Warnings */}
              {preview.warnings.length > 0 && (
                <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <Icon name="AlertTriangle" size={16} className="text-amber-600 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                        {t('Warnings')}
                      </p>
                      <ul className="text-xs text-amber-700 dark:text-amber-300 space-y-0.5">
                        {preview.warnings.map((w, i) => (
                          <li key={i}>{w}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* CO-644: falling back to the dialog default is never silent — format drives work
                  points and referee shift length, so a blank column can move real money. */}
              {analysis && (analysis.rowsUsingDefaultFormat > 0 || analysis.rowsUsingDefaultType > 0) && (
                <section
                  className="border border-border bg-muted/40 rounded-lg p-4 flex items-start gap-2"
                  data-testid="bulk-import-defaults-notice"
                >
                  <Icon name="Info" size={16} className="text-muted-foreground shrink-0 mt-0.5" />
                  <div className="space-y-1 text-sm">
                    <p className="font-medium text-foreground">
                      {t('Some matches will use the import settings below, not the sheet')}
                    </p>
                    <ul className="text-xs text-muted-foreground space-y-0.5">
                      {analysis.rowsUsingDefaultFormat > 0 && (
                        <li>
                          {t('{{count}} matches have no Format cell and will be created as the default format.', {
                            count: analysis.rowsUsingDefaultFormat,
                          })}
                        </li>
                      )}
                      {analysis.rowsUsingDefaultType > 0 && (
                        <li>
                          {t('{{count}} matches have no Type cell and will be created as the default type.', {
                            count: analysis.rowsUsingDefaultType,
                          })}
                        </li>
                      )}
                    </ul>
                    <p className="text-xs text-muted-foreground">
                      {t('Format decides referee work points and how long the match blocks their schedule, so set it per row when it varies.')}
                    </p>
                  </div>
                </section>
              )}

              {analysis && analysis.approximateMatches?.length > 0 && (
                <ApproximateMatchesPanel matches={analysis.approximateMatches} t={t} />
              )}

              {/* CO-643: who will NOT make it onto a match, stated before the operator commits. */}
              {analysis && analysis.unresolvedOfficials?.length > 0 && (
                <UnresolvedOfficialsPanel officials={analysis.unresolvedOfficials} t={t} />
              )}

              {/* Preview table */}
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="max-h-96 overflow-auto">
                  <table className="w-full min-w-max text-sm whitespace-nowrap" data-testid="bulk-import-preview-table">
                    <thead className="bg-muted/50 sticky top-0">
                      <tr>
                        <th scope="col" className="relative px-3 py-2 text-start text-xs font-medium text-muted-foreground">
                          <span aria-hidden="true">#</span>
                          <span className="sr-only">{t('Row number')}</span>
                        </th>
                        <th scope="col" className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('Status')}</th>
                        <th scope="col" className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('Day')}</th>
                        <th scope="col" className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('Time')}</th>
                        <th scope="col" className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('Venue')}</th>
                        <th scope="col" className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('Pitch')}</th>
                        <th scope="col" className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('Age Category')}</th>
                        <th scope="col" className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('Division')}</th>
                        {hasColumn.format && (
                          <th scope="col" className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('Format')}</th>
                        )}
                        {hasColumn.type && (
                          <th scope="col" className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('Type')}</th>
                        )}
                        <th scope="col" className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('Team 1')}</th>
                        <th scope="col" className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('Team 2')}</th>
                        <th scope="col" className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('Commissioner')}</th>
                        {hasColumn.headRef && (
                          <th scope="col" className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('Head Referee')}</th>
                        )}
                        {hasColumn.ar1 && (
                          <th scope="col" className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('Assistant Referee 1')}</th>
                        )}
                        {hasColumn.ar2 && (
                          <th scope="col" className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('Assistant Referee 2')}</th>
                        )}
                        {hasColumn.fourth && (
                          <th scope="col" className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">{t('4th Official')}</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {preview.rows.map((row, i) => {
                        const rowResult = resultsByRow.get(i + 1);
                        return (
                          <tr key={i} className="hover:bg-muted/30">
                            <td className="px-3 py-2 text-muted-foreground">{i + 1}</td>
                            <td className="px-3 py-2">
                              <div className="flex flex-wrap items-center gap-1">
                                <ActionBadge result={rowResult} t={t} />
                                {isRowCancelled(row) && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">
                                    <Icon name="Ban" size={11} />
                                    {t('Cancelled')}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2">{row.day}</td>
                            <td className="px-3 py-2">
                              {row.time
                                ? row.time
                                : <span className="text-muted-foreground italic">{t('Time TBD')}</span>}
                            </td>
                            <td className="px-3 py-2">{row.venue}</td>
                            <td className="px-3 py-2">{row.pitch || '-'}</td>
                            <td className="px-3 py-2">{row.ageCategory || '-'}</td>
                            <td className="px-3 py-2">{row.division || '-'}</td>
                            {hasColumn.format && (
                              <td className="px-3 py-2">
                                {row.format || <span className="text-muted-foreground italic">{t('Default')}</span>}
                              </td>
                            )}
                            {hasColumn.type && (
                              <td className="px-3 py-2">
                                {row.type || <span className="text-muted-foreground italic">{t('Default')}</span>}
                              </td>
                            )}
                            <td className="px-3 py-2 font-medium">{row.team1Name}</td>
                            <td className="px-3 py-2 font-medium">{row.team2Name}</td>
                            <td className="px-3 py-2">{row.commissioner || '-'}</td>
                            {hasColumn.headRef && (
                              <td className="px-3 py-2">{row.headReferee || '-'}</td>
                            )}
                            {hasColumn.ar1 && (
                              <td className="px-3 py-2">{row.assistantReferee1 || '-'}</td>
                            )}
                            {hasColumn.ar2 && (
                              <td className="px-3 py-2">{row.assistantReferee2 || '-'}</td>
                            )}
                            {hasColumn.fourth && (
                              <td className="px-3 py-2">{row.fourthOfficial || '-'}</td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Configuration */}
              <div className="space-y-3 border-t border-border pt-4">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Icon name="Settings" size={16} className="text-primary" />
                  {t('Import Settings')}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label htmlFor="bulk-import-week-start-date" className="block text-xs font-medium text-foreground mb-1">
                      {t('Week Start Date')} *
                    </label>
                    <Input
                      id="bulk-import-week-start-date"
                      type="date"
                      required
                      value={weekStartDate}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWeekStartDate(e.target.value)}
                      data-testid="bulk-import-week-start-date"
                    />
                  </div>
                  <div>
                    <label htmlFor="bulk-import-default-format" className="block text-xs font-medium text-foreground mb-1">
                      {t('Default Format')}
                    </label>
                    <Select
                      id="bulk-import-default-format"
                      aria-label={t('Default Format')}
                      value={defaultFormat}
                      onChange={(v: string) => setDefaultFormat(v)}
                      options={[
                        { value: String(MatchFormat.FiveAside), label: t('5 aside') },
                        { value: String(MatchFormat.SevenAside), label: t('7 aside') },
                        { value: String(MatchFormat.NineAside), label: t('9 aside') },
                        { value: String(MatchFormat.ElevenAside), label: t('11 aside') },
                      ]}
                      data-testid="bulk-import-default-format"
                    />
                  </div>
                  <div>
                    <label htmlFor="bulk-import-default-type" className="block text-xs font-medium text-foreground mb-1">
                      {t('Default Type')}
                    </label>
                    <Select
                      id="bulk-import-default-type"
                      aria-label={t('Default Type')}
                      value={defaultType}
                      onChange={(v: string) => setDefaultType(v)}
                      options={[
                        { value: String(MatchType.Outdoor), label: t('Outdoor') },
                        { value: String(MatchType.Indoor), label: t('Indoor') },
                      ]}
                      data-testid="bulk-import-default-type"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Phase 3: Result */}
          {phase === 'result' && result && (
            <div className="space-y-4">
              {/* Success summary */}
              <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <Icon name="CheckCircle" size={24} className="text-green-600 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-green-800 dark:text-green-200">
                      {t('Import Complete')}
                    </p>
                    <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                      {t('Created {{count}} matches', { count: result.created })}
                      {result.updated > 0 && ` · ${t('{{count}} updated', { count: result.updated })}`}
                      {result.skipped > 0 && ` · ${t('{{count}} skipped', { count: result.skipped })}`}
                      {result.warningCount > 0 && ` · ${t('{{count}} with warnings', { count: result.warningCount })}`}
                    </p>
                  </div>
                </div>
              </div>

              {result.approximateMatches?.length > 0 && (
                <ApproximateMatchesPanel matches={result.approximateMatches} t={t} />
              )}

              {/* CO-643: the same list after the fact, so it can still be acted on. */}
              {result.unresolvedOfficials?.length > 0 && (
                <UnresolvedOfficialsPanel officials={result.unresolvedOfficials} t={t} />
              )}

              {/* Warnings — rows that imported but need a look */}
              {warnedRows.length > 0 && (
                <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <Icon name="AlertTriangle" size={16} className="text-amber-600 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                        {t('{{count}} imported with warnings', { count: warnedRows.length })}
                      </p>
                      <ul className="text-xs text-amber-700 dark:text-amber-300 space-y-1 max-h-48 overflow-auto">
                        {warnedRows.map((r) => (
                          <li key={r.rowIndex}>
                            <span className="font-medium">{t('Row {{n}}', { n: r.rowIndex })}:</span>{' '}
                            {/* Server-authored text (English today) sits inside an RTL list item; without an
                                isolate the bidi algorithm tears its leading quote off (measured in Arabic). */}
                            <bdi>{r.warnings.join('; ')}</bdi>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Errors */}
              {result.errors.length > 0 && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <Icon name="AlertCircle" size={16} className="text-destructive shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-destructive">
                        {t('{{count}} errors occurred', { count: result.errors.length })}
                      </p>
                      <ul className="text-xs text-destructive/80 space-y-0.5 max-h-40 overflow-auto">
                        {result.errors.map((err, i) => (
                          <li key={i}><bdi>{err}</bdi></li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Error display */}
          {error && (
            <div role="alert" className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 flex items-center gap-2">
              <Icon name="AlertCircle" size={16} className="text-destructive shrink-0" />
              <p className="text-sm text-destructive"><bdi>{error}</bdi></p>
            </div>
          )}
        </DialogBody>

        {/* ── Footer ── */}
        <DialogFooter className="py-4 shrink-0">
          {phase === 'upload' && (
            <Button
              type="button"
              onClick={handleClose}
              className="px-5 py-2 border border-border text-foreground rounded-lg hover:bg-muted"
              data-testid="bulk-import-cancel-btn"
            >
              {t('Cancel')}
            </Button>
          )}

          {phase === 'preview' && (
            <>
              <Button
                type="button"
                onClick={() => {
                  setPhase('upload');
                  setPreview(null);
                  setSelectedFile(null);
                  setError('');
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className="px-5 py-2 border border-border text-foreground rounded-lg hover:bg-muted"
                data-testid="bulk-import-back-btn"
              >
                {t('Back')}
              </Button>
              <Button
                type="button"
                onClick={handleImport}
                disabled={isLoading || !weekStartDate}
                className="px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="bulk-import-confirm-btn"
              >
                <Icon
                  name={isLoading ? 'Loader2' : 'Upload'}
                  size={16}
                  className={isLoading ? 'animate-spin' : ''}
                />
                {isLoading
                  ? t('Importing...')
                  : analysis
                    ? t('Apply ({{n}} new, {{u}} update)', { n: analysis.newCount, u: analysis.updateCount })
                    : t('Import {{count}} Matches', { count: preview?.validRows ?? 0 })}
              </Button>
            </>
          )}

          {phase === 'result' && (
            <Button
              type="button"
              onClick={handleDone}
              className="px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2"
              data-testid="bulk-import-done-btn"
            >
              <Icon name="Check" size={16} />
              {t('Done')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Action Badge ───────────────────────────────────────────────────────────

// CO-643 — a name that matched only by tolerating a spelling difference puts a REAL person into a
// paid slot on the strength of a guess. That belongs in front of the operator before they commit,
// not in a badge tooltip a keyboard, screen-reader or touch user can never reach.
const ApproximateMatchesPanel: React.FC<{
  matches: ApproximateMatchView[];
  t: (key: string, options?: Record<string, unknown>) => string;
}> = ({ matches, t }) => {
  // One row per distinct (sheet spelling -> person), however many rows repeat it.
  const rows = useMemo(() => {
    const byPair = new Map<string, { entry: ApproximateMatchView; rows: number[] }>();
    matches.forEach((m) => {
      const key = `${m.name} ${m.matchedName} ${m.roleLabel}`;
      const seen = byPair.get(key);
      if (seen) seen.rows.push(m.rowIndex);
      else byPair.set(key, { entry: m, rows: [m.rowIndex] });
    });
    return [...byPair.values()];
  }, [matches]);

  if (rows.length === 0) return null;

  return (
    <section
      className="border border-border bg-muted/40 rounded-lg p-4 space-y-3"
      aria-labelledby="bulk-import-approx-heading"
      data-testid="bulk-import-approximate"
    >
      <div className="flex items-start gap-2">
        <Icon name="SearchCheck" size={16} className="text-muted-foreground shrink-0 mt-0.5" />
        <div className="space-y-0.5">
          <h3 id="bulk-import-approx-heading" className="text-sm font-semibold text-foreground">
            {t('{{count}} names matched a referee with a different spelling', { count: rows.length })}
          </h3>
          <p className="text-xs text-muted-foreground">
            {t('These people will be assigned to the match. Check each one before importing.')}
          </p>
        </div>
      </div>
      <ul className="space-y-1 text-xs">
        {rows.map(({ entry, rows: at }, i) => (
          <li key={`${entry.name}-${entry.matchedName}-${i}`} className="flex flex-wrap items-baseline gap-x-1.5">
            <span className="text-muted-foreground">{entry.name}</span>
            <Icon name="ArrowRight" size={12} className="text-muted-foreground rtl:rotate-180" aria-hidden="true" />
            <span className="font-medium text-foreground">{entry.matchedName}</span>
            <span className="text-muted-foreground">
              {t(entry.roleLabel)}
              {'⁨'} · {at.length === 1
                ? t('Row {{n}}', { n: at[0] })
                : t('{{count}} rows', { count: at.length })}{'⁩'}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
};

// ── Unresolved officials ───────────────────────────────────────────────────
//
// CO-643. A name in an official column that the importer cannot place produces no assignment,
// and used to say so only as one line inside a per-row warning list — easy to scroll past, so
// operators reasonably believed the person had been added. This panel states it once, up front,
// grouped by what the operator has to DO about it.

// Ordered by actionability: the ones the operator can fix come first, the two expected outcomes last.
const UNRESOLVED_ORDER: UnresolvedOfficialReason[] = [
  UnresolvedOfficialReason.NotAReferee,
  UnresolvedOfficialReason.Ambiguous,
  UnresolvedOfficialReason.UnknownName,
  UnresolvedOfficialReason.DuplicateRole,
  UnresolvedOfficialReason.MatchCancelled,
  UnresolvedOfficialReason.AlreadyOnMatch,
];

// The last two are not mistakes, so they are stated in the muted register rather than the amber one.
const EXPECTED_REASONS = new Set<UnresolvedOfficialReason>([
  UnresolvedOfficialReason.DuplicateRole,
  UnresolvedOfficialReason.MatchCancelled,
  UnresolvedOfficialReason.AlreadyOnMatch,
]);

// A candidate spelled exactly like the sheet cell is not a near miss (the server echoes the
// matched employee for NotAReferee), and "Close to: <same name>" reads as a typo that is not there.
const nearMisses = (o: UnresolvedOfficialView): string[] => {
  const own = o.name.trim().toLowerCase();
  return o.candidates.filter((c) => c.trim().toLowerCase() !== own);
};

const UnresolvedOfficialsPanel: React.FC<{
  officials: UnresolvedOfficialView[];
  t: (key: string, options?: Record<string, unknown>) => string;
}> = ({ officials, t }) => {
  const groups = useMemo(() => {
    const byReason = new Map<UnresolvedOfficialReason, UnresolvedOfficialView[]>();
    officials.forEach((o) => {
      // The backend serializes enums by NAME ("NotAReferee", …) via StringEnumConverter, so the
      // raw value never equals the numeric member. Grouping on it directly silently produced zero
      // groups and rendered nothing at all.
      const reason = resolveEnum(UnresolvedOfficialReason, o.reason) as UnresolvedOfficialReason | undefined;
      if (reason === undefined) return;
      const bucket = byReason.get(reason);
      if (bucket) bucket.push(o);
      else byReason.set(reason, [o]);
    });
    return UNRESOLVED_ORDER
      .filter((reason) => byReason.has(reason))
      .map((reason) => {
        // Collapse repeats of the same person in the same role: a sheet naming one mistyped
        // referee on every row is ONE problem to fix, not N.
        const byPerson = new Map<string, { entry: UnresolvedOfficialView; rows: number[] }>();
        byReason.get(reason)!.forEach((o) => {
          const key = `${o.name} ${o.roleLabel}`;
          const seen = byPerson.get(key);
          if (seen) seen.rows.push(o.rowIndex);
          else byPerson.set(key, { entry: o, rows: [o.rowIndex] });
        });
        return { reason, items: [...byPerson.values()] };
      });
  }, [officials]);

  // The headline counts distinct people, matching what is actually listed.
  const distinctCount = useMemo(
    () => new Set(officials.map((o) => `${o.name} ${o.roleLabel}`)).size,
    [officials],
  );

  if (groups.length === 0) return null;

  const explain = (reason: UnresolvedOfficialReason): string => {
    switch (reason) {
      case UnresolvedOfficialReason.NotAReferee:
        return t('Employed here, but with no referee profile. Add them under Referee Management, then import again.');
      case UnresolvedOfficialReason.Ambiguous:
        return t('The spelling fits more than one referee, so none was chosen. Write the name exactly as it appears in Referee Management.');
      case UnresolvedOfficialReason.UnknownName:
        return t('No one in this company matches this name.');
      case UnresolvedOfficialReason.DuplicateRole:
        return t('Already taken by an earlier role on the same row, so only the first was kept.');
      case UnresolvedOfficialReason.MatchCancelled:
        return t('The match is cancelled, so its officials were left unassigned.');
      case UnresolvedOfficialReason.AlreadyOnMatch:
        return t('Already on this match under another role, so the second role was not added.');
      default:
        return '';
    }
  };

  return (
    <section
      className="border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 rounded-lg p-4 space-y-4"
      aria-labelledby="bulk-import-unresolved-heading"
      data-testid="bulk-import-unresolved"
    >
      <div className="flex items-start gap-2">
        <Icon name="UserX" size={16} className="text-amber-600 shrink-0 mt-0.5" />
        <div className="space-y-0.5">
          <h3
            id="bulk-import-unresolved-heading"
            className="text-sm font-semibold text-amber-900 dark:text-amber-100"
          >
            {t('{{count}} officials will not be added to a match', { count: distinctCount })}
          </h3>
          <p className="text-xs text-amber-800 dark:text-amber-300">
            {t('The matches themselves still import. Only these assignments are skipped.')}
          </p>
        </div>
      </div>

      <dl className="space-y-3">
        {groups.map(({ reason, items }) => {
          const expected = EXPECTED_REASONS.has(reason);
          return (
            <div key={reason} className="space-y-1.5">
              <dt
                className={`text-xs ${
                  expected
                    ? 'text-amber-700/80 dark:text-amber-400/80'
                    : 'text-amber-800 dark:text-amber-300 font-medium'
                }`}
              >
                {explain(reason)}
              </dt>
              <dd>
                <ul className="flex flex-wrap gap-1.5">
                  {items.map(({ entry: o, rows }, i) => (
                    <li
                      key={`${reason}-${o.name}-${o.roleLabel}-${i}`}
                      className={`inline-flex flex-wrap items-baseline gap-x-1.5 px-2 py-1 rounded-md border text-xs ${
                        expected
                          ? 'border-border bg-background text-muted-foreground'
                          : 'border-amber-300 dark:border-amber-700 bg-background text-foreground'
                      }`}
                    >
                      <span className="font-medium break-words">{o.name}</span>
                      <span className="text-muted-foreground">
                        {/* roleLabel is a fixed English constant from the server; every value
                            already has a key in all three locales. */}
                        {t(o.roleLabel)}
                        {'⁨'} · {rows.length === 1
                          ? t('Row {{n}}', { n: rows[0] })
                          : t('{{count}} rows', { count: rows.length })}{'⁩'}
                      </span>
                      {nearMisses(o).length > 0 && (
                        // The collisions are the single most useful fact for an ambiguous name, so
                        // they are VISIBLE text, not a title= tooltip a keyboard or touch user
                        // can never reach.
                        <span className="w-full text-muted-foreground">
                          {t('Close to: {{names}}', { names: nearMisses(o).join(', '), nsSeparator: false })}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </dd>
            </div>
          );
        })}
      </dl>
    </section>
  );
};

const ActionBadge: React.FC<{
  result: BulkImportRowResult | undefined;
  t: (key: string) => string;
}> = ({ result, t }) => {
  if (!result) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-muted text-muted-foreground">
        {t('—')}
      </span>
    );
  }
  // The backend serializes enums by name ("New", "Update", …); resolveEnum
  // normalizes either form to the numeric member so the switch matches.
  const action = resolveEnum(BulkImportRowAction, result.action);
  const cfg = (() => {
    switch (action) {
      case BulkImportRowAction.New:
        return { label: t('New'), className: 'bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-300' };
      case BulkImportRowAction.Update:
        return { label: t('Update'), className: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300' };
      case BulkImportRowAction.Skip:
        return { label: t('Skip'), className: 'bg-muted text-muted-foreground' };
      case BulkImportRowAction.Error:
      default:
        return { label: t('Error'), className: 'bg-destructive/10 text-destructive' };
    }
  })();
  const warnings = result.warnings ?? [];
  const badgeTooltip = [result.message, ...warnings].filter(Boolean).join('\n') || undefined;
  return (
    <span className="inline-flex items-center gap-1">
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${cfg.className}`}
        title={badgeTooltip}
      >
        {cfg.label}
      </span>
      {warnings.length > 0 && (
        <span
          className="inline-flex items-center text-amber-600"
          title={warnings.join('\n')}
          aria-label={t('Has warnings')}
        >
          <Icon name="AlertTriangle" size={12} />
        </span>
      )}
    </span>
  );
};

export default BulkImportModal;
